import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  getMe: () => api.get("/auth/me"),
};

// Systems
export const systemsAPI = {
  getAll: () => api.get("/systems"),
  getOne: (id) => api.get(`/systems/${id}`),
  create: (data) => api.post("/systems", data),
  update: (id, data) => api.patch(`/systems/${id}`, data),
  delete: (id) => api.delete(`/systems/${id}`),
};

// Scans
export const scansAPI = {
  getAll: () => api.get("/scans"),
  create: (data) => api.post("/scans", data),
  delete: (id) => api.delete(`/scans/${id}`),
};

// Events
export const eventsAPI = {
  getAll: () => api.get("/events"),
  getOne: (id) => api.get(`/events/${id}`),
  create: (data) => api.post("/events", data),
  update: (id, data) => api.patch(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  // Event Groups
  createGroup: (eventId, data) => api.post(`/events/${eventId}/groups`, data),
  updateGroup: (eventId, groupId, data) => api.patch(`/events/${eventId}/groups/${groupId}`, data),
  deleteGroup: (eventId, groupId) => api.delete(`/events/${eventId}/groups/${groupId}`),
  addSystemToGroup: (eventId, groupId, systemId) => api.post(`/events/${eventId}/groups/${groupId}/systems/${systemId}`),
  removeSystemFromGroup: (eventId, groupId, systemId) => api.delete(`/events/${eventId}/groups/${groupId}/systems/${systemId}`),
  // Venue Integration
  importVenueConfig: (eventId, venueId) => api.post(`/events/${eventId}/import-venue-config/${venueId}`),
  getByVenue: (venueId) => api.get(`/events/by-venue/${venueId}`),
};

// Frequencies
export const frequenciesAPI = {
  assign: (data) => api.post("/frequencies/assign", data),
};

// IEM Equipment
export const equipmentAPI = {
  getAll: () => api.get("/equipment"),
  getOne: (id) => api.get(`/equipment/${id}`),
  getBands: (id) => api.get(`/equipment/${id}/bands`),
  getChannels: (id) => api.get(`/equipment/${id}/channels`),
  assignFrequencies: (data) => api.post("/equipment/assign-frequencies", data),
  getDetails: (id) => api.get(`/equipment/details/${id}`),
  importFromWWB: (data) => api.post("/equipment/import-from-wwb", data),
  getAllImported: () => api.get("/equipment/all-imported"),
  deleteImported: (id) => api.delete(`/equipment/imported/${id}`),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
  getSpectrum: () => api.get("/spectrum/live"),
};

// Templates
export const templatesAPI = {
  getAll: () => api.get("/templates"),
  getOne: (id) => api.get(`/templates/${id}`),
  apply: (data) => api.post("/templates/apply", data),
  getCustom: () => api.get("/templates/user/saved"),
  saveCustom: (data) => api.post("/templates/save-custom", data),
  deleteCustom: (id) => api.delete(`/templates/custom/${id}`),
};

// Reports
export const reportsAPI = {
  downloadPDF: (eventId) => 
    api.get(`/reports/pdf/${eventId}`, { responseType: "blob" }),
  downloadQuickPDF: () =>
    api.get("/reports/quick-pdf", { responseType: "blob" }),
};

// Subscriptions
export const subscriptionsAPI = {
  getPlans: () => api.get("/subscriptions/plans"),
  getMySubscription: () => api.get("/subscriptions/my-subscription"),
  getPaymentHistory: () => api.get("/subscriptions/payment-history"),
};

// Chile Frequencies
export const chileFrequenciesAPI = {
  getRegions: () => api.get("/frequencies/chile/regions"),
  getRegionDetail: (regionId) => api.get(`/frequencies/chile/regions/${regionId}`),
  getRecommendations: () => api.get("/frequencies/chile/recommendations"),
  searchAvailable: (regionId, numChannels) => 
    api.get(`/frequencies/chile/search?region_id=${regionId}&num_channels=${numChannels}`),
};

// Notifications
export const notificationsAPI = {
  test: () => api.post("/notifications/test"),
};

// Admin
export const adminAPI = {
  getStats: () => api.get("/admin/stats"),
  getUsers: (planFilter, search) => {
    const params = new URLSearchParams();
    if (planFilter) params.append("plan_filter", planFilter);
    if (search) params.append("search", search);
    return api.get(`/admin/users?${params.toString()}`);
  },
  createUser: (data) => api.post("/admin/users", data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUserSubscription: (userId, planId) => 
    api.patch(`/admin/users/${userId}/subscription?plan_id=${planId}`),
  updateUserRole: (userId, role) =>
    api.patch(`/admin/users/${userId}/role?role=${role}`),
  getPayments: (statusFilter) => {
    const params = statusFilter ? `?status_filter=${statusFilter}` : "";
    return api.get(`/admin/payments${params}`);
  },
};

// Contact
export const contactAPI = {
  submitEquipmentRequest: (data) => api.post("/contact/equipment-request", data),
  submitGeneral: (data) => api.post("/contact/general", data),
};

// Venues
export const venuesAPI = {
  getAll: () => api.get("/venues"),
  getOne: (id) => api.get(`/venues/${id}`),
  create: (data) => api.post("/venues", data),
  update: (id, data) => api.put(`/venues/${id}`, data),
  delete: (id) => api.delete(`/venues/${id}`),
  load: (id) => api.post(`/venues/${id}/load`),
  saveFrequencies: (id, frequencies) => api.post(`/venues/${id}/save-frequencies`, frequencies),
};

// Organizations
export const organizationsAPI = {
  getMyOrganization: () => api.get("/organizations/my-organization"),
  create: (data) => api.post("/organizations/create", data),
  inviteUser: (data) => api.post("/organizations/invite", data),
  acceptInvitation: (token) => api.post("/organizations/accept-invitation", { token }),
  getInvitationInfo: (token) => axios.get(`${API_BASE}/organizations/invitation/${token}`),
  updateMemberRole: (memberId, role) => api.patch(`/organizations/members/${memberId}/role`, { role }),
  removeMember: (memberId) => api.delete(`/organizations/members/${memberId}`),
  cancelInvitation: (invitationId) => api.delete(`/organizations/invitations/${invitationId}`),
};

export default api;
